import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { LP_UI_CONFIG } from '../core/tokens';
import { StorageService } from '../services/storage.service';

export interface PushSubscription {
  endpoint: string;
  keys: {
    auth: string;
    p256dh: string;
  };
}

export interface NotificationSubscriptionRequest extends PushSubscription {
  host: string;
}

export interface NotificationPayload {
  notification: {
    title: string;
    body: string;
    icon: string;
    vibrate: number[];
    data: {
      dateOfArrival: number;
      primaryKey: number;
    };
    actions: {
      action: string;
      title: string;
    }[];
  };
}

@Injectable({ providedIn: 'root' })
export class NotificationApi {
  private readonly cfg = inject(LP_UI_CONFIG);
  private http = inject(HttpClient);
  private storage = inject(StorageService);

  private get headers() {
    return {
      Authorization: 'Bearer ' + this.storage.token(),
    };
  }

  /**
   * Subscribe to push notifications
   * @param subscription - Push subscription object
   * @param host - Application host URL
   * @returns Promise with subscription result
   */
  async subscribeToPush(subscription: PushSubscription, host: string): Promise<void> {
    const request: NotificationSubscriptionRequest = {
      ...subscription,
      host,
    };

    return firstValueFrom(
      this.http.post<void>(`${this.cfg.host}api/notification/subscribe`, request, {
        headers: this.headers,
      })
    );
  }

  /**
   * Acknowledge received notification
   * @param notificationID - Notification ID
   * @returns Promise with acknowledgement result
   */
  async acknowledgeNotification(notificationID: number): Promise<void> {
    return firstValueFrom(
      this.http.post<void>(
        `${this.cfg.host}api/notification/acknowledge/${notificationID}`,
        {},
        { headers: this.headers }
      )
    );
  }

  /**
   * Send test notification
   * @param payload - Notification payload
   * @returns Promise with test result
   */
  async sendTestNotification(payload: NotificationPayload): Promise<void> {
    return firstValueFrom(
      this.http.post<void>(`${this.cfg.host}api/notification/test`, payload, {
        headers: this.headers,
      })
    );
  }

  async getVapidPublicKey(): Promise<string> {
    const response = await firstValueFrom(
      this.http.get<{ vapIdPublicKey: string }>(`${this.cfg.host}api/authenticated_frontend_keys`, {
        headers: this.headers,
      })
    );
    return response.vapIdPublicKey;
  }
}
